/**
 * MED-1348: the page to open after sign-in when the router state is gone.
 *
 * ProtectedRoute passes the attempted URL as router state, but the 401
 * handler in services/api/client.ts does a full-page redirect to login, which
 * drops that state. An expired session on an email deep link then landed on
 * the dashboard. sessionStorage is per tab and survives the reload.
 *
 * Only staff pages are kept: persona users sign in by email link in a new tab,
 * so a saved page cannot reach them. The page belongs to one user, so another
 * person who signs in on the same tab goes to their own home.
 */
const KEY = 'login-return-to';
const NOT_A_RETURN_PREFIXES = [
  '/login',
  '/coach',
  '/partner',
  '/participant',
  '/auth',
  '/accept-invitation',
  '/forgot-password',
  '/reset-password',
  '/book',
  '/sessions',
];

function isStaffPage(pathname: string): boolean {
  if (pathname === '/') return false;
  return !NOT_A_RETURN_PREFIXES.some(
    p => pathname === p || pathname.startsWith(`${p}/`)
  );
}

export function saveLoginReturn(
  userId: string,
  pathname: string,
  search: string
): void {
  if (!isStaffPage(pathname)) return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ userId, path: pathname + search })
    );
  } catch {
    // Storage blocked: sign-in still works, it opens the home page.
  }
}

/** The saved page, only for the user who saved it. */
export function readLoginReturn(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as {
      userId?: string;
      path?: string;
    } | null;
    return saved?.userId === userId && saved.path ? saved.path : null;
  } catch {
    return null;
  }
}

export function clearLoginReturn(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}
